/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { CustomDialog } from "@/components/custom/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCopyMediaProduct } from "@/hooks/campaign-hooks/useCoppyMediaProducts";
import { useGetCampaigns } from "@/hooks/campaign-hooks/useGetCampaigns";
import { useCreateCampaign } from "@/hooks/campaign-hooks/useCreateCampaign";
import { useTypedTranslations } from "@/i18n/typed";
import { isEmptyObject } from "@/lib/utils";
import { useCampaignStore } from "@/stores/campaignStore";
import { useUser } from "@/stores/user";
import { PlusIcon } from "lucide-react";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { useForm } from "react-hook-form";
import CampaignDialogAddCompleted from "./CampaignDialogAddCompleted";
import { environments } from "@/lib/config";

export default function CampaignDialog() {
  const router = useRouter();
  const authServerUrl = environments.authServerUrl;
  const t = useTypedTranslations("dialogs");
  const { handleSubmit, setValue, watch } = useForm();
  const campaign = watch("campaign");
  const { openCampaignDialog, mediaProductSelected, setOpenCampaignDialog } =
    useCampaignStore();
  const { userInfo } = useUser();
  const isLogin = !isEmptyObject(userInfo?.user);
  const [addCampaignCompleted, setAddCampaignCompleted] = useState(false);

  const { data: campaignDataRes } = useGetCampaigns(
    {
      page: 1,
      page_size: 20,
      sort_order: "newest",
    },
    isLogin,
  );

  const [checkAddMedia, setCheckAddMedia] = useState(true);

  const { mutate: copyMedia } = useCopyMediaProduct();
  const { mutate: createCampaign } = useCreateCampaign();

  const { data: campaignData } = campaignDataRes || {};

  const handleCreateNewCampaignAndCopy = () => {
    createCampaign(
      { campaign_name: "" },
      {
        onSuccess: (response) => {
          const newCampaignId = (response as any)?.data?.campaign_id;

          if (newCampaignId) {
            copyMedia(
              {
                campaign_id: parseInt(newCampaignId.toString()),
                media_product_ids: mediaProductSelected.map((id) =>
                  parseInt(id),
                ),
              },
              {
                onSuccess: (res) => {
                  if (!res?.data.added_count) {
                    setCheckAddMedia(false);
                  }
                  setAddCampaignCompleted(true);
                  setOpenCampaignDialog(false);
                },
              },
            );
          }
        },
      },
    );
  };

  const onSubmit = (data: any) => {
    if (data?.campaign) {
      copyMedia(
        {
          campaign_id: parseInt(data?.campaign),
          media_product_ids: mediaProductSelected.map((id) => parseInt(id)),
        },
        {
          onSuccess: (res) => {
            if (!res?.data.added_count) {
              setCheckAddMedia(false);
            }
            setAddCampaignCompleted(true);
            setOpenCampaignDialog(false);
          },
        },
      );
    }

    //onClose();
  };

  return (
    <>
      <CustomDialog
        size="xs"
        cancelText=""
        okText=""
        title={
          isLogin ? (
            <span className="text-[#424242] text-[24px] leading-[132%] tracking-[-0.096px]">
              {t("campaignDialog.title")}
            </span>
          ) : (
            <>{t("campaignDialog.titleLoginRequired")}</>
          )
        }
        description={
          isLogin ? (
            <span className="gap-2 font-medium leading-[140%] tracking-[-0.064px]">
              {t("campaignDialog.description")}
            </span>
          ) : (
            <>{t("campaignDialog.descriptionLoginRequired")}</>
          )
        }
        open={openCampaignDialog}
        onOpenChange={setOpenCampaignDialog}
      >
        {isLogin ? (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {!addCampaignCompleted && (
              <div className="space-y-2">
                <Label
                  htmlFor="campaign"
                  className="font-normal leading-[120%] tracking-[-0.048px] mb-1"
                >
                  {t("campaignDialog.campaignLabel")}
                </Label>
                <Select onValueChange={(value) => setValue("campaign", value)}>
                  <SelectTrigger
                    className="font-medium leading-[140%] tracking-[-0.064px] border-gray-500"
                    classNameIcon="text-[#222]"
                  >
                    <SelectValue
                      placeholder={t("campaignDialog.campaignPlaceholder")}
                    />
                  </SelectTrigger>
                  <SelectContent classNameContent="p-0">
                    {campaignData?.map((item) => (
                      <SelectItem
                        key={item.campaign_id}
                        value={item.campaign_id?.toString()}
                        className="border-b border-gray-400 w-full p-3 hover:bg-gray-100"
                      >
                        {item.campaign_name}
                      </SelectItem>
                    ))}
                    {/* <SelectItem value="new">나이키 캠페인</SelectItem>
                      <SelectItem value="campaign1">아디다스 </SelectItem> */}
                    <div className="m-2">
                      <Button
                        size="sm"
                        onClick={handleCreateNewCampaignAndCopy}
                        variant="secondary"
                        tone="light"
                        className="w-full h-9"
                      >
                        <PlusIcon className="size-4" />{" "}
                        {t("campaignDialog.createNewCampaignButton")}
                      </Button>
                    </div>
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#BDBDBD] leading-[120%] tracking-[ -0.048px]">
                  {t("campaignDialog.helpText")}
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="secondary"
                tone="light"
                onClick={() => setOpenCampaignDialog(false)}
                className="px-5 py-4 text-[#616161] leading-[120%] tracking-[-0.064px]"
              >
                {t("campaignDialog.cancelText")}
              </Button>
              <Button
                type="submit"
                className="px-5 py-4leading-[120%] tracking-[-0.064px]"
                disabled={!campaign}
              >
                {t("campaignDialog.addText")}
              </Button>
            </div>
          </form>
        ) : (
          <div className="flex justify-end gap-2  -mb-4">
            <Button
              type="button"
              variant="secondary"
              tone="light"
              onClick={() => setOpenCampaignDialog(false)}
            >
              {t("campaignDialog.cancelText")}
            </Button>
            <Button
              onClick={() => {
                router.push(authServerUrl ?? "/account/login");
              }}
              type="button"
            >
              {t("campaignDialog.goToLoginText")}
            </Button>
          </div>
        )}
      </CustomDialog>
      <CampaignDialogAddCompleted
        open={addCampaignCompleted}
        checkAddMedia={checkAddMedia}
        onClose={() => setAddCampaignCompleted(false)}
      />
    </>
  );
}
